/**
 * /my-coaching/business/nova — NOVA Home. The coach's account state picks the
 * Home (`novaHomeVariant`).
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { CalendarReconnectNotice } from "~/components/calendar-reconnect-notice";
import { NovaHome } from "~/components/nova/lifecycle-home/nova-home";
import { novaHomeVariant } from "~/components/nova/lifecycle-home/view-model";
import { Skeleton } from "~/components/ui/skeleton";
import { callsAndLinksQueryOptions } from "~/lib/queries/callsAndLinksQuery";
import { coachAccountQueryOptions } from "~/lib/queries/coachAccountQuery";
import {
  helpVideosQueryOptions,
  myCourseWeekQueryOptions,
  placementVideosQueryOptions,
} from "~/lib/queries/learningQuery";
import {
  novaAssetsQueryOptions,
  novaHeldWorkQueryOptions,
} from "~/lib/queries/novaAssetsQuery";
import {
  myClientRevenueEstimateQueryOptions,
  myReadinessQueryOptions,
} from "~/lib/queries/readinessQuery";
import { BUSINESS_BUILDER_COURSE } from "~/lib/shared/learning/business-builder-outline";

export const Route = createFileRoute("/_user/my-coaching/business/nova/")({
  staticData: { title: "NOVA" },
  loader: async ({ context }) => {
    const userId = context.user.clerkUserId;
    const variant = novaHomeVariant(context.account?.lifecycle ?? null);
    // Optimisations that hand SSR each Home's rows, not their data source:
    // every block reads the same queries and shows its own failure.
    const { queryClient } = context;
    const prefetch = (() => {
      switch (variant) {
        case "unavailable":
          return [];
        case "pre_trial":
          return [
            queryClient.ensureQueryData(placementVideosQueryOptions()),
            queryClient.ensureQueryData(helpVideosQueryOptions()),
            queryClient.ensureQueryData(myReadinessQueryOptions()),
            queryClient.ensureQueryData(callsAndLinksQueryOptions()),
            queryClient.ensureQueryData(myClientRevenueEstimateQueryOptions()),
          ];
        case "trial":
          return [
            queryClient.ensureQueryData(placementVideosQueryOptions()),
            queryClient.ensureQueryData(helpVideosQueryOptions()),
            queryClient.ensureQueryData(
              myCourseWeekQueryOptions(BUSINESS_BUILDER_COURSE.slug, 1),
            ),
            queryClient.ensureQueryData(callsAndLinksQueryOptions()),
          ];
        case "paid":
          return [
            queryClient.ensureQueryData(novaAssetsQueryOptions(userId)),
            queryClient.ensureQueryData(helpVideosQueryOptions()),
            queryClient.ensureQueryData(callsAndLinksQueryOptions()),
            queryClient.ensureQueryData(myReadinessQueryOptions()),
            queryClient.ensureQueryData(myClientRevenueEstimateQueryOptions()),
          ];
        case "winback":
          return [
            queryClient.ensureQueryData(novaHeldWorkQueryOptions(userId)),
          ];
        default: {
          const exhaustive: never = variant;
          throw new Error(`Unknown NOVA Home variant: ${String(exhaustive)}`);
        }
      }
    })();
    await Promise.all(prefetch.map((read) => read.catch(() => undefined)));
    return { userId, firstName: context.coach.firstName ?? null };
  },
  component: NovaHomeRoute,
});

function NovaHomeRoute() {
  const { userId, firstName } = Route.useLoaderData();
  // A return from checkout refreshes this read before the page opens (the
  // Nova layout's re-sync), so the cached account is the one to follow.
  const {
    data: account,
    refetch,
    isFetching,
    isPending,
  } = useQuery(coachAccountQueryOptions(userId));
  // The layout primes the account, so this waits only when that read failed
  // and the query is trying again on its own.
  if (isPending && isFetching) return <Skeleton className="h-64 rounded-2xl" />;
  const variant = novaHomeVariant(account?.lifecycle ?? null);

  return (
    <div className="space-y-8">
      <CalendarReconnectNotice />
      <NovaHome
        account={{
          suspended: account?.suspended ?? false,
          winbackTrialEligible: account?.winbackTrialEligible ?? false,
        }}
        firstName={firstName}
        onRetry={() => void refetch()}
        retrying={isFetching}
        userId={userId}
        variant={variant}
      />
    </div>
  );
}
